import { NatsConnection, StringCodec, JSONCodec } from "nats.ws";
import { Kvm, KV } from "@nats-io/kv";

export class NatsKV {
    private nc: NatsConnection;
    private kvm: Kvm;
    private bucket: KV | null = null;
    private sc = StringCodec();
    private jc = JSONCodec();
    private watchers: (() => void)[] = [];

    constructor(nc: NatsConnection) {
        this.nc = nc;
        this.kvm = new Kvm(nc as any);
    }

    /**
     * Seleziona (o crea) uno specifico bucket.
     * @param bucketName Nome del bucket
     * @param options Opzioni di configurazione (es. history)
     */
    async selectBucket(
        bucketName: string, 
        options: { history?: number, storage?: any, timeout?: number } = { history: 5 }
    ) {
        this.bucket = await this.kvm.create(bucketName, options);
    }

    /**
     * Restituisce la lista dei nomi dei bucket disponibili.
     */
    async listBuckets(): Promise<string[]> {
        const buckets: string[] = [];
        const iter = this.kvm.list();
        for await (const status of iter) {
            buckets.push(status.bucket);
        }
        return buckets;
    }

    /**
     * Inserisce un valore con una specifica chiave.
     * @param key Chiave
     * @param value Valore (stringa o oggetto)
     */
    async put(key: string, value: any) {
        if (!this.bucket) throw new Error("Nessun bucket selezionato");
        
        let encodedValue: Uint8Array;
        if (typeof value === 'string') {
             encodedValue = this.sc.encode(value);
        } else {
             encodedValue = this.jc.encode(value);
        }
        
        return await this.bucket.put(key, encodedValue);
    }

    /**
     * Preleva un valore con una specifica chiave.
     * @param key Chiave
     */
    async get(key: string): Promise<any | null> {
        if (!this.bucket) throw new Error("Nessun bucket selezionato");
        const entry = await this.bucket.get(key);
        if (!entry) return null;
        return this.decodeValue(entry.value);
    }

    /**
     * Rimane in ascolto su uno specifico subject (pattern di chiavi).
     * @param keyPattern Pattern della chiave (es. "user.*" o ">")
     * @param callback Funzione chiamata ad ogni aggiornamento
     * @returns Una funzione per fermare l'ascolto
     */
    async watch(keyPattern: string, callback: (key: string, value: any, operation: string) => void) {
        if (!this.bucket) throw new Error("Nessun bucket selezionato");

        const watcher = await this.bucket.watch({ key: keyPattern });
        
        // Avvia il loop di ascolto in background
        (async () => {
            for await (const e of watcher) {
                const key = e.key;
                const operation = e.operation; // PUT, DEL, PURGE
                let value = null;
                
                if (e.value && operation === "PUT") {
                    value = this.decodeValue(e.value);
                }
                
                callback(key, value, operation);
            }
        })();

        const stop = () => watcher.stop();
        this.watchers.push(stop);
        return stop;
    }

    private decodeValue(value: Uint8Array): any {
        try {
            // Proviamo a decodificare come JSON
            return this.jc.decode(value);
        } catch {
            // Se fallisce, ritorniamo come stringa semplice
            return this.sc.decode(value);
        }
    }

    /**
     * Ferma tutti i watcher e pulisce le risorse.
     */
    destroy() {
        this.watchers.forEach(stop => stop())
        this.watchers = []
        this.kvm = null
        this.bucket = null
    }
}
